import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import { Box, IconButton, Typography } from '@mui/material';
import { grey, lightBlue } from '@mui/material/colors';
import PatientTravelWorklistApi from 'api/patient/PatientTravelWorklistApi';
import ModalBoxes from 'common/feedback/ModalBoxes/ModalBoxes';
import NotificationManager from 'common/notifications/NotificationManager';
import { dateTime12, DD_SLASH_MMM_SLASH_YYYY } from 'constants/dateFormat';
import { sortBy } from 'lodash';
import { has } from 'lodash/object';
import moment from 'moment-timezone';

import {
  TR_BOOKED,
  TR_PENDING,
  travelServiceTypeMap,
  travelStatusMap
} from 'components/pages/patient-source/Patients/PatientInfo/EncountersSection/TravelSection/travelConstants';
import { generateUrlByKey } from 'components/root/router';

import EditNoteModal from '../shared/worklist-table-note/EditNoteModal/EditNoteModal';
import ViewNoteModal from '../shared/worklist-table-note/ViewNoteModal/ViewNoteModal';

import AssigneeChangeDropdown from './AssigneeChangeDropdown/AssigneeChangeDropdown';
import StatusChangeDropdown from './StatusChangeDropdown/StatusChangeDropdown';

export default function useDataGrid() {
  const [gridData, setGridData] = useState([]);

  const columns = useMemo(
    () => [
      {
        field: 'patientFullName',
        headerName: 'Patient',
        flex: 1,
        renderCell({ value, row }) {
          return (
            <Link
              to={generateUrlByKey('Patients.Patient Profile.Patient Studies', {
                patientId: row.patientId,
                ssuPatientId: row.ssuPatientId
              })}
            >
              {value}
            </Link>
          );
        }
      },
      {
        field: 'clientVisitStartTime',
        headerName: 'Visit Date time',
        flex: 1,
        valueGetter(value) {
          return value ? new Date(value) : null;
        },
        renderCell({ value }) {
          return value ? moment(value).format(dateTime12) : '';
        },
        type: 'date',
        align: 'center',
        minWidth: 180
      },
      {
        field: 'submissionTime',
        headerName: 'Submission time',
        flex: 1,
        valueGetter(value) {
          return value ? new Date(value) : null;
        },
        renderCell({ value }) {
          return value ? moment(value).format(dateTime12) : '';
        },
        type: 'date',
        align: 'center',
        minWidth: 180
      },
      {
        field: 'submittedByName',
        headerName: 'Submitted by',
        flex: 1
      },
      {
        field: 'bookedAt',
        headerName: 'Booked date',
        flex: 1,
        valueGetter(value) {
          return value ? new Date(value) : null;
        },
        renderCell({ value }) {
          return value ? moment(value).format(DD_SLASH_MMM_SLASH_YYYY) : '';
        },
        type: 'date',
        align: 'center'
      },
      {
        field: 'bookedByName',
        headerName: 'Booked by',
        flex: 1
      },
      {
        field: 'studyName',
        headerName: 'Study',
        flex: 1
      },
      {
        field: 'siteName',
        headerName: 'Site',
        flex: 1
      },
      {
        field: 'encounterName',
        headerName: 'Encounter',
        flex: 1,
        renderCell({ value, row }) {
          return (
            <Link
              to={generateUrlByKey(
                'Travel Requests.Travel Request',
                {
                  patientId: row.patientId,
                  ssuPatientId: row.ssuPatientId,
                  travelRequestId: row.id
                },
                {
                  ssuPatientEncounterId: row.ssuPatientEncounterId,
                  protocolEncounterKey: row.protocolEncounterKey
                }
              )}
            >
              {value}
            </Link>
          );
        }
      },
      {
        field: 'subjectId',
        headerName: 'SubjectID',
        flex: 1
      },
      {
        field: 'requestedServices',
        headerName: 'Type of Travel',
        flex: 1,
        valueGetter(value) {
          return value ? sortBy(value.map(type => travelServiceTypeMap[type])).join(' & ') : '';
        },
        renderCell({ value, row }) {
          return (
            <Link
              to={generateUrlByKey(
                'Travel Requests.Travel Request',
                {
                  patientId: row.patientId,
                  ssuPatientId: row.ssuPatientId,
                  travelRequestId: row.id
                },
                {
                  ssuPatientEncounterId: row.ssuPatientEncounterId,
                  protocolEncounterKey: row.protocolEncounterKey
                }
              )}
            >
              {value}
            </Link>
          );
        }
      },
      {
        field: 'assigneePersonnelName',
        headerName: 'Assigned',
        flex: 1,
        minWidth: 200,
        renderCell({ row }) {
          if (row?.status !== TR_BOOKED) {
            return (
              <AssigneeChangeDropdown
                row={row}
                onChangeSuccess={function(data) {
                  setGridData(function(state) {
                    return state.map(function(r) {
                      if (r.id !== row.id) return r;
                      return {
                        ...r,
                        ...data
                      };
                    });
                  });
                }}
              />
            );
          }
          return (
            <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
              <Typography variant="body1">{row?.assigneePersonnelName || ''}</Typography>
            </Box>
          );
        }
      },
      {
        field: 'note',
        headerName: 'Note',
        flex: 1,
        maxWidth: 100,
        renderCell({ value, row }) {
          return (
            <IconButton
              onClick={function() {
                ModalBoxes.open({
                  component:
                    row.status !== TR_BOOKED ? (
                      <EditNoteModal
                        row={row}
                        onSave={function(note, markAsRead) {
                          return PatientTravelWorklistApi.updateWorklistRow(row.id, {
                            note,
                            markAsRead
                          });
                        }}
                      />
                    ) : (
                      <ViewNoteModal row={row} />
                    ),
                  onClose(data) {
                    if (has(data, 'note') && has(data, 'isNoteRead')) {
                      setGridData(function(state) {
                        return state.map(function(r) {
                          if (r.id !== row.id) return r;
                          return {
                            ...r,
                            ...data
                          };
                        });
                      });
                    }
                  }
                });
              }}
            >
              {value && !row?.isNoteRead ? (
                <MarkEmailUnreadIcon data-testid="MarkEmailUnreadIcon" htmlColor={lightBlue[500]} />
              ) : (
                <EmailIcon data-testid="EmailIcon" htmlColor={grey[500]} />
              )}
            </IconButton>
          );
        }
      },
      {
        field: 'status',
        headerName: 'Action',
        flex: 1,
        minWidth: 200,
        valueGetter(value) {
          return travelStatusMap[value] || '';
        },
        renderCell({ value, row }) {
          if (row.status === TR_PENDING) {
            return (
              <StatusChangeDropdown
                row={row}
                onChangeSuccess={function(data) {
                  NotificationManager.success(`Request status was updated to ${travelStatusMap[data?.status]}`);
                  setGridData(function(state) {
                    return state.map(function(r) {
                      if (r.id !== row.id) return r;
                      return { ...r, ...data };
                    });
                  });
                }}
              />
            );
          }
          return (
            <Box sx={{ display: 'flex', height: '100%', alignItems: 'center' }}>
              <Typography variant="body1">{value}</Typography>
            </Box>
          );
        }
      }
    ],
    []
  );

  return [columns, gridData, setGridData];
}
